const initQuizWidget = () => {
  const dataEl = document.getElementById('quiz-widget-data');
  if (!dataEl) return;

  const parseData = () => {
    try {
      return JSON.parse(dataEl.textContent || '{}');
    } catch (e) {
      console.warn('Quiz widget: unable to parse data', e);
      return {};
    }
  };

  const data = parseData();
  const questions = (data.questions || [])
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((q, idx) => ({
      ...q,
      __key: q && q.id != null ? String(q.id) : `idx-${idx}`
    }));
  const config = data.config || {};
  const customer = data.customer || {};
  const modal = document.querySelector('[data-quiz-modal]');
  const contentEl = modal ? modal.querySelector('[data-quiz-content]') : null;
  const progressEl = modal ? modal.querySelector('[data-quiz-progress]') : null;
  const nextBtn = modal ? modal.querySelector('[data-quiz-next]') : null;
  const backBtn = modal ? modal.querySelector('[data-quiz-back]') : null;
  const closeEls = modal ? modal.querySelectorAll('[data-quiz-close]') : [];

  if (!modal || !contentEl || !nextBtn || !backBtn || !progressEl) return;

  const state = {
    step: 0,
    answers: {},
    result: null,
    lastResult: customer.lastResult || null,
    selectedResults: {}
  };

  const localStorageKey = 'quiz_widget_last_result';

  const normalizeHandle = (val) => {
    if (val == null) return '';
    const str = String(val).trim();
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const normalizeId = (val) => {
    if (val == null) return '';
    const str = String(val).trim();
    if (!str) return '';
    const match = str.match(/Product\/(\d+)/i);
    return match ? match[1] : str;
  };

  const normalizeProductKey = (val) => {
    const idKey = normalizeId(val);
    const handleKey = normalizeHandle(val);
    return idKey || handleKey;
  };

  const getResultKey = (entry, idx) => {
    const product = entry?.product || {};
    return (
      entry?.__key ||
      normalizeProductKey(product.handle || product.id) ||
      normalizeHandle(product.title) ||
      `idx-${idx}`
    );
  };

  const getCurrentQuestion = () => questions[state.step];

  const setProgress = () => {
    const totalSteps = questions.length;
    const label = modal.querySelector('[data-quiz-step-label]');
    const progressContainer = progressEl.parentElement;

    if (!totalSteps) {
      progressEl.style.width = '0%';
      if (progressContainer) progressContainer.style.setProperty('--quiz-progress', '0%');
      if (label) label.textContent = '';
      return;
    }

    const pct = ((Math.min(state.step + 1, totalSteps)) / totalSteps) * 100;
    const clampedPct = Math.min(100, Math.max(0, pct));
    progressEl.style.width = `${clampedPct}%`;
    if (progressContainer) progressContainer.style.setProperty('--quiz-progress', `${clampedPct}%`);
    if (label) {
      const current = Math.min(state.step + 1, totalSteps);
      label.textContent = `Question ${current} of ${totalSteps}`;
    }
  };

  const closeModal = () => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quiz-modal-open');
  };

  const openModal = (opts = {}) => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quiz-modal-open');
    if (state.lastResult) {
      renderResult(state.lastResult);
    } else {
      state.step = 0;
      state.answers = {};
      renderStep();
    }
  };

  const saveLocalResult = (payload) => {
    try {
      localStorage.setItem(localStorageKey, JSON.stringify(payload));
    } catch (e) {
      console.warn('Quiz widget: unable to save local result', e);
    }
  };

  const clearLocalResult = () => {
    try {
      localStorage.removeItem(localStorageKey);
    } catch (e) {
      /* no-op */
    }
  };

  const loadLocalResult = () => {
    try {
      const raw = localStorage.getItem(localStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const lastSavedResult = loadLocalResult();

  const trySaveCustomerMetafield = async (payload) => {
    if (!customer.isLoggedIn) return;
    const mutation = `
      mutation customerUpdate($input: CustomerUpdateInput!) {
        customerUpdate(input: $input) {
          customer { id }
          userErrors { field message }
        }
      }
    `;
    const variables = {
      input: {
        metafields: [{
          namespace: "quiz",
          key: "last_result",
          type: "json",
          value: JSON.stringify(payload)
        }]
      }
    };
    try {
      await fetch('/account/api/2023-10/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: mutation, variables })
      });
    } catch (e) {
      console.warn('Quiz widget: could not save customer metafield', e);
    }
  };

  const parseQuestionData = (question) => {
    let parsed = {};
    if (!question || question.data == null) return { answers: [] };
    if (typeof question.data === 'object') {
      parsed = question.data;
    } else {
      try {
        parsed = JSON.parse(question.data);
      } catch (e) {
        parsed = {};
      }
    }
    // Some metaobject JSON may come as stringified JSON inside value
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        parsed = {};
      }
    }
    const answers = Array.isArray(parsed.answers) ? parsed.answers : [];
    // Normalize weights into array of {handle, value}
    const normalized = answers.map((ans) => {
      const weightsObj = ans.weights || {};
      const weightsList = Object.entries(weightsObj).map(([handle, value]) => ({
        handle,
        value: Number(value) || 0
      }));
      return { ...ans, weightsList };
    });
    return { answers: normalized };
  };

  const buildProductLookup = () => {
    const lookup = {};

    const addProductToLookup = (product, keySource = null) => {
      const normalizedHandle = normalizeHandle(product?.handle);
      const normalizedId = normalizeId(product?.id);
      const base = { ...product };
      if (normalizedHandle && !base.handle) base.handle = normalizedHandle;
      if (normalizedId && !base.id) base.id = normalizedId;

      const keys = [
        normalizeProductKey(keySource),
        normalizedHandle,
        normalizedId
      ].filter(Boolean);

      keys.forEach((k) => {
        if (!lookup[k]) lookup[k] = base;
      });
    };

    (config.productList || []).forEach((p) => {
      addProductToLookup(
        {
          ...p,
          handle: normalizeHandle(p?.handle) || normalizeHandle(p?.id) || p?.handle,
          id: normalizeId(p?.id) || p?.id
        },
        p?.handle || p?.id
      );
    });
    questions.forEach((q) => {
      const parsed = parseQuestionData(q);
      (parsed.answers || []).forEach((a) => {
        (a.weightsList || []).forEach((w) => {
          const key = normalizeProductKey(w.handle);
          if (!key) return;
          addProductToLookup({ handle: normalizeHandle(w.handle) || key }, key);
        });
      });
    });
    return lookup;
  };

  const productLookup = buildProductLookup();
  const productCache = {};

  const computeScores = () => {
    const scores = {};
    Object.keys(state.answers).forEach((questionKey) => {
      const selectedAnswerId = state.answers[questionKey];
      const question = questions.find((q) => String(q.__key) === String(questionKey));
      if (!question) return;
      const parsed = parseQuestionData(question);
      const answer = parsed.answers[Number(selectedAnswerId)];
      if (!answer) return;
      (answer.weightsList || []).forEach((w) => {
        const key = normalizeProductKey(w.handle);
        if (!key) return;
        scores[key] = (scores[key] || 0) + (w.value || 0);
      });
    });

    const entries = Object.entries(scores).map(([key, score]) => {
      const normalizedHandle = normalizeHandle(key);
      const normalizedId = normalizeId(key);
      const p =
        productLookup[key] ||
        (normalizedHandle ? productLookup[normalizedHandle] : null) ||
        (normalizedId ? productLookup[normalizedId] : null) ||
        { handle: normalizedHandle || normalizedId || key };
      return { product: p, score };
    }).sort((a, b) => b.score - a.score);

    const top = entries.slice(0, config.maxSuggestions || 3);
    return { scores: entries, top };
  };

  const renderQuestion = (question, answers = []) => {
    const questionKey = question.__key;
    const wrapper = document.createElement('div');
    wrapper.className = 'quiz-question';

    const title = document.createElement('div');
    title.className = 'quiz-question__title h2';
    title.textContent = question.title || 'Question';
    wrapper.appendChild(title);

    if (question.description) {
      const desc = document.createElement('div');
      desc.className = 'quiz-question__description';
      desc.innerHTML = question.description;
      wrapper.appendChild(desc);
    }

    const answersWrap = document.createElement('div');
    answersWrap.className = 'quiz-answers';
    answers.forEach((answer, idx) => {
      const answerId = String(idx);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-answer checkbox-ctrl';
      btn.textContent = answer.label || 'Answer';
      if (String(state.answers[questionKey]) === answerId) {
        btn.classList.add('is-selected');
      }
      btn.addEventListener('click', () => {
        // toggle selection to allow deselect
        if (String(state.answers[questionKey]) === answerId) {
          delete state.answers[questionKey];
        } else {
          state.answers[questionKey] = answerId;
        }
        renderStep(); // rerender to update selection and buttons
      });
      answersWrap.appendChild(btn);
    });
    wrapper.appendChild(answersWrap);
    return wrapper;
  };

  const fetchProduct = async (handle) => {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return null;
    if (productCache[normalizedHandle]) return productCache[normalizedHandle];
    try {
      const res = await fetch(`/products/${normalizedHandle}.js`);
      if (!res.ok) throw new Error('fetch failed');
      const json = await res.json();
      const variants = json.variants || [];
      const availableVariant =
        variants.find((v) => v.available && (v.inventory_quantity == null || v.inventory_quantity > 0)) ||
        variants.find((v) => v.available) ||
        variants.find((v) => v.inventory_quantity > 0) ||
        variants[0] ||
        {};
      const mapped = {
        handle: json.handle,
        title: json.title,
        image: json.images?.[0],
        variantId: availableVariant.id,
        available: Boolean(availableVariant.available),
        inventoryQuantity: availableVariant.inventory_quantity
      };
      productCache[normalizedHandle] = mapped;
      return mapped;
    } catch (e) {
      return null;
    }
  };

  const ensureProductInfo = async (entries) => {
    const updated = await Promise.all((entries || []).map(async (entry) => {
      const p = entry.product || {};
      if (p.title && p.image && p.variantId) return entry;
      const fetched = await fetchProduct(p.handle || p.id);
      if (fetched) {
        const normalizedHandle = normalizeHandle(p.handle) || fetched.handle;
        return {
          ...entry,
          product: {
            ...p,
            ...fetched,
            handle: normalizedHandle || p.id || fetched.handle
          }
        };
      }
      return entry;
    }));
    return updated;
  };

  const renderResult = async (result) => {
    state.result = result;
    const enrichedTop = await ensureProductInfo(result.top);
    result.top = (enrichedTop || []).map((entry, idx) => ({
      ...entry,
      __key: getResultKey(entry, idx)
    }));
    state.selectedResults = {};
    result.top.forEach((entry, idx) => {
      state.selectedResults[getResultKey(entry, idx)] = true;
    });
    contentEl.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'quiz-result';

    const heading = document.createElement('div');
    heading.className = 'quiz-question__title';
    heading.textContent = 'Your candle matches';
    container.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'quiz-result__list';
    (result.top || []).forEach((entry) => {
      const itemKey = getResultKey(entry);
      const item = document.createElement('div');
      item.className = 'quiz-result__item';

      const img = document.createElement('img');
      img.src = entry.product?.image || '//cdn.shopify.com/s/images/admin/no-image-large.gif';
      img.alt = entry.product?.title + ' candle' || '';
      item.appendChild(img);

      const meta = document.createElement('div');
      const selectLabel = document.createElement('label');
      selectLabel.className = 'checkbox-ctrl sj-bundle__item quiz-result__select';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = state.selectedResults[itemKey] !== false;
      checkbox.setAttribute(
        'aria-label',
        `Add ${entry.product?.title || entry.product?.handle || 'product'} to cart`
      );
      checkbox.addEventListener('change', () => {
        state.selectedResults[itemKey] = checkbox.checked;
        updateAddBtnState();
      });
      const checkboxText = document.createElement('span');
      checkboxText.textContent = entry.product?.title || entry.product?.handle || 'Product';
      selectLabel.appendChild(checkbox);
      selectLabel.appendChild(checkboxText);
      meta.appendChild(selectLabel);
      // no score requested in UI
      item.appendChild(meta);

      item.addEventListener('click', (e) => {
        if (e.target === checkbox || e.target.closest('label.quiz-result__select')) return;
        checkbox.checked = !checkbox.checked;
        state.selectedResults[itemKey] = checkbox.checked;
        updateAddBtnState();
      });

      list.appendChild(item);
    });
    container.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'cta__flex';
    const addBtn = document.createElement('button');
    addBtn.className = 'button button--primary quiz-modal__btn quiz-modal__btn--primary';
    addBtn.textContent = 'Add selected to cart';
    addBtn.addEventListener('click', () => {
      const selectedKeys = Object.keys(state.selectedResults || {}).filter((key) => state.selectedResults[key]);
      addTopProductsToCart(result.top || [], { addBtn, selectedKeys });
    });
    const retakeBtn = document.createElement('button');
    retakeBtn.className = 'button quiz-modal__btn quiz-modal__btn--ghost';
    retakeBtn.textContent = 'Retake quiz';
    retakeBtn.type = 'button';
    retakeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      state.step = 0;
      state.answers = {};
      state.result = null;
      state.lastResult = null;
      state.selectedResults = {};
      clearLocalResult();
      renderStep();
    });
    actions.appendChild(addBtn);
    actions.appendChild(retakeBtn);
    const status = document.createElement('div');
    status.className = 'quiz-result__status';
    status.setAttribute('aria-live', 'polite');
    status.dataset.quizStatus = 'true';
    actions.appendChild(status);
    container.appendChild(actions);

    contentEl.appendChild(container);
    nextBtn.disabled = true;
    backBtn.style.display = 'none';
    nextBtn.style.display = 'none';
    progressEl.style.width = '100%';
    const label = modal.querySelector('[data-quiz-step-label]');
    if (label) label.textContent = '';

    const updateAddBtnState = () => {
      const selectedCount = Object.values(state.selectedResults || {}).filter(Boolean).length;
      addBtn.disabled = selectedCount === 0;
      if (selectedCount === 0) {
        status.textContent = 'Select at least one candle to add.';
      } else if (status.textContent === 'Select at least one candle to add.') {
        status.textContent = '';
      }
    };
    updateAddBtnState();
  };

  const addTopProductsToCart = async (top, { addBtn, selectedKeys } = {}) => {
    const statusEl = modal.querySelector('[data-quiz-status]');
    const setStatus = (msg) => {
      if (statusEl) statusEl.textContent = msg;
    };

    const selectedSet = Array.isArray(selectedKeys) ? new Set(selectedKeys) : null;
    const entries = await ensureProductInfo(top);
    const selectedEntries = selectedSet
      ? (entries || []).filter((entry, idx) => selectedSet.has(getResultKey(entry, idx)))
      : entries;
    const availableSelected = (selectedEntries || []).filter((t) => {
      const p = t.product || {};
      const hasStock = p.inventoryQuantity == null ? p.available !== false : (p.inventoryQuantity || 0) > 0;
      return p.variantId && hasStock;
    });
    const items = availableSelected
      .map((t) => t.product?.variantId ? { id: Number(t.product.variantId), quantity: 1 } : null)
      .filter(Boolean);
    if (!items.length) {
      setStatus('No selected in-stock items to add right now.');
      return;
    }
    if (addBtn) addBtn.disabled = true;
    setStatus('Adding to cart…');
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      window.location = '/cart';
    } catch (e) {
      console.warn('Quiz widget: could not add to cart', e);
      if (addBtn) addBtn.disabled = false;
      setStatus('Could not add to cart. Please try again.');
    }
  };

  const renderStep = () => {
    state.result = null;
    const question = getCurrentQuestion();
    if (!question) {
      contentEl.textContent = 'No questions found.';
      return;
    }
    const parsed = parseQuestionData(question);
    contentEl.innerHTML = '';
    contentEl.appendChild(renderQuestion(question, parsed.answers));
    const isFirst = state.step === 0;
    const isLast = state.step >= questions.length - 1;
    backBtn.disabled = isFirst;
    nextBtn.disabled = state.answers[question.__key] == null;
    nextBtn.textContent = isLast ? 'See results' : 'Next';
    setProgress();
    backBtn.style.display = '';
    nextBtn.style.display = '';
  };

  const goNext = () => {
    if (state.step >= questions.length - 1) {
      const result = computeScores();
      state.lastResult = result;
      saveLocalResult(result);
      trySaveCustomerMetafield(result);
      renderResult(result);
      return;
    }
    state.step += 1;
    renderStep();
  };

  const goBack = () => {
    if (state.step === 0) return;
    state.step -= 1;
    renderStep();
  };

  nextBtn.addEventListener('click', goNext);
  backBtn.addEventListener('click', goBack);
  closeEls.forEach((el) => el.addEventListener('click', closeModal));
  modal.addEventListener('mousedown', (e) => {
    // Close when clicking outside the dialog (on the backdrop/root)
    if (e.target === modal) {
      closeModal();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) {
      closeModal();
    }
  });

  const hrefMatchesQuiz = (href) => {
    if (!href) return false;
    const lower = href.toLowerCase();
    return lower === '#quiz' || lower === '#find-your-candle' || lower.endsWith('/#quiz') || lower.endsWith('/#find-your-candle');
  };

  const isTriggerEl = (el) => {
    if (!el) return false;
    const href = (el.getAttribute('href') || '');
    const text = (el.textContent || '').trim().toLowerCase();
    return (
      el.classList.contains('quiz-modal-trigger') ||
      hrefMatchesQuiz(href) ||
      text === 'find your candle'
    );
  };

  const attachTriggers = () => {
    // Direct binding for existing elements
    const selector = [
      'a.quiz-modal-trigger',
      'button.quiz-modal-trigger',
      'a[href="#quiz"]',
      'a[href="#find-your-candle"]'
    ].join(',');
    document.querySelectorAll(selector).forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });

    // Delegated binding for any future/dynamic links
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a,button');
      if (!target) return;
      if (isTriggerEl(target)) {
        e.preventDefault();
        openModal();
      }
    });
  };

  // Initialize
  if (!state.lastResult && lastSavedResult) {
    state.lastResult = lastSavedResult;
  }

  attachTriggers();
  if (state.lastResult) {
    renderResult(state.lastResult);
  } else {
    renderStep();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuizWidget);
} else {
  initQuizWidget();
}
